// @ts-nocheck
import * as React from "react";
import { useEscapeKey } from "./useEscapeKey";

// Shared "confirm destructive action" modal that shows the live impact
// (counts of rows that would be detached/nullified) before the operator
// commits. Used by region-management and organization-management to keep
// the confirmation UX identical.
//
// Props:
//   resourceLabel   — e.g. "권역", "기관"
//   subject         — { code?, name, badge? } shown in the header
//   impact          — { label → count } map. Null while loading.
//   loading         — true while fetching impact preview
//   detachedNote    — short Korean explanation of what "분리됨" means here
//   transferOptions — optional [{id, label}] of targets to move sub-resources to.
//                     When omitted, the modal behaves exactly like before.
//   onCancel
//   onConfirm       — called as onConfirm({ transferToId }) when transferOptions
//                     is provided, else as onConfirm() for backward compat.
//
// The button only enables once the impact has loaded so the operator
// never confirms blind.
export function DeletionConfirmModal({
  resourceLabel,
  subject,
  impact,
  loading,
  detachedNote = "해제된 데이터는 추후 재배치하거나 별도로 정리해야 합니다.",
  transferOptions,
  // deletable=false면 이력 보존을 위해 하드 삭제를 막는다(예: 방문·매칭이 있는 매니저).
  // 이때 삭제 버튼을 감추고 blockedNote로 대체 조치(비활성 등)를 안내한다.
  deletable = true,
  blockedNote = "연결된 이력이 있어 삭제할 수 없습니다.",
  onCancel,
  onConfirm,
}) {
  useEscapeKey(onCancel);
  const blocked   = !loading && deletable === false;
  const hasImpact = !loading && impact && Object.values(impact).some(([_, v]) => v > 0);
  const empty     = !loading && impact && Object.values(impact).every(([_, v]) => v === 0);
  const [transferToId, setTransferToId] = React.useState("");
  const supportsTransfer = Array.isArray(transferOptions) && transferOptions.length > 0;
  const confirmLabel = supportsTransfer && transferToId
    ? `이관 후 삭제`
    : `삭제 진행`;

  return (
    <div onClick={onCancel}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:60}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:480, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)'}}>
          <div className="h3" style={{color:'var(--rose-500)'}}>{resourceLabel} 삭제</div>
          <div className="muted" style={{fontSize:11, marginTop:2}}>
            {subject.code && <b>{subject.code} · </b>}{subject.name} {resourceLabel}을(를) 삭제합니다.
          </div>
        </div>

        <div style={{padding:'18px 22px'}}>
          {loading ? (
            <div className="muted" style={{fontSize:12.5}}>연관 데이터를 확인하는 중…</div>
          ) : blocked ? (
            <>
              {hasImpact && (
                <>
                  <div style={{fontSize:12.5, color:'var(--ink-1)', marginBottom:10}}>이 {resourceLabel}에 남아 있는 이력:</div>
                  <div style={{display:'grid', gridTemplateColumns:`repeat(${Math.min(Object.keys(impact).length, 4)}, 1fr)`, gap:10, marginBottom:12}}>
                    {Object.entries(impact).map(([key, [label, count]]) => (
                      <div key={key} style={{padding:'12px 10px', background:'var(--bg-sub)', borderRadius:10, textAlign:'center'}}>
                        <div className="muted" style={{fontSize:10, fontWeight:600}}>{label}</div>
                        <div className="mono" style={{fontSize:18, fontWeight:800, color: count > 0 ? 'var(--rose-500)' : 'var(--ink-3)'}}>{count}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="muted" style={{fontSize:12, padding:'10px 12px', background:'var(--rose-50, #fdecec)', color:'var(--rose-600, #a3322f)', borderRadius:8, whiteSpace:'pre-line', lineHeight:1.5}}>
                {blockedNote}
              </div>
            </>
          ) : hasImpact ? (
            <>
              <div style={{fontSize:12.5, color:'var(--ink-1)', marginBottom:10}}>다음 데이터는 {resourceLabel} 연결이 해제됩니다 (삭제되지는 않음):</div>
              <div style={{display:'grid', gridTemplateColumns:`repeat(${Math.min(Object.keys(impact).length, 4)}, 1fr)`, gap:10}}>
                {Object.entries(impact).map(([key, [label, count]]) => (
                  <div key={key} style={{padding:'12px 10px', background:'var(--bg-sub)', borderRadius:10, textAlign:'center'}}>
                    <div className="muted" style={{fontSize:10, fontWeight:600}}>{label}</div>
                    <div className="mono" style={{fontSize:18, fontWeight:800, color: count > 0 ? 'var(--rose-500)' : 'var(--ink-3)'}}>
                      {count}
                    </div>
                  </div>
                ))}
              </div>
              <div className="muted" style={{fontSize:11, marginTop:12, padding:'8px 10px', background:'var(--amber-50)', color:'#8a6326', borderRadius:8, whiteSpace:'pre-line'}}>
                ⚠ {transferToId ? `선택한 ${resourceLabel}으로 위 데이터가 일괄 이관됩니다.` : detachedNote}
              </div>

              {supportsTransfer && (
                <div style={{marginTop:14}}>
                  <div className="label" style={{marginBottom:6}}>이관 대상 {resourceLabel} (선택)</div>
                  <select className="select" value={transferToId}
                    onChange={e => setTransferToId(e.target.value)} style={{width:'100%'}}>
                    <option value="">— 미지정 (연결만 해제) —</option>
                    {transferOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <div className="muted" style={{fontSize:10.5, marginTop:6}}>
                    대상을 선택하면 분리 대신 해당 {resourceLabel}으로 옮긴 뒤 삭제합니다.
                  </div>
                </div>
              )}
            </>
          ) : empty ? (
            <div style={{fontSize:12.5, color:'var(--ink-1)'}}>이 {resourceLabel}에 연결된 데이터가 없습니다. 안전하게 삭제할 수 있습니다.</div>
          ) : null}
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button className="btn btn-ghost btn-md" onClick={onCancel}>{blocked ? "닫기" : "취소"}</button>
          {!blocked && (
            <button className="btn btn-md"
              onClick={() => supportsTransfer ? onConfirm({ transferToId: transferToId ? Number(transferToId) : null }) : onConfirm()}
              disabled={loading}
              style={{background:'var(--rose-500)', color:'#fff'}}>{confirmLabel}</button>
          )}
        </div>
      </div>
    </div>
  );
}
